<template>
    <div class="main-wrapper">
        <el-row type="flex" class="mb-1 align-center">
            <b class="section-subheader">Contact</b>
        </el-row>
        <el-row>
            <span>Control whether contact changes are sent to {{ integrationNameWithCasing }}, received from {{ integrationNameWithCasing }}, or both by selecting the appropriate direction.</span>
        </el-row>
        <el-row class="mt-1">
          <span><b>Recommended/ Default values</b>: Both {{ integrationNameWithCasing }} and {{ staticName }} <b>can update</b> Contacts information (<i
              class="fas fa-arrows-alt-h"></i>), but a good practice used, {{ staticName }} <b>cannot create</b> new Contacts (<i class="fas fa-arrow-left"></i>) in {{ integrationNameWithCasing }}.</span>
        </el-row>

        <el-row :gutter="20" class="mt-3">
            <!-- Start Aloware and HubSpot Labels -->
            <el-col :span="10" class="mb-1">
                <small>{{ staticName }}</small>
            </el-col>
            <el-col :span="4" class="mb-1 text-center">
                <small>Direction</small>
            </el-col>
            <el-col :span="10" class="mb-1">
                <small>{{ integrationNameWithCasing }}</small>
            </el-col>
            <!-- End Aloware and HubSpot Labels -->
        </el-row>

        <el-row v-for="(operation, index) in model.settings.mapper_settings.contact" :key="operation.internal_value" :gutter="20" type="flex" align="middle" class="mb-3">
            <!-- Start Aloware Fields -->
            <el-col :span="10">
                <el-input
                    readonly
                    :value="operation.internal_label"
                    class="w-100"
                />
            </el-col>
            <!-- End Aloware Fields -->

            <el-col :span="4" class="text-center">
                <direction-selector
                    :initial-state="operation.direction"
                    @change="direction => updateDirection(index, direction)"
                    :disabled="operation.direction_fixed"
                    :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                >
                </direction-selector>
            </el-col>

            <!-- Start CRM Fields -->
            <el-col :span="10">
                <el-input
                    readonly
                    :value="operation.crm_label"
                    class="w-100"
                />
            </el-col>
            <!-- End CRM Fields -->
        </el-row>
    </div>
</template>

<script>
import DirectionSelector from '../direction-selector.vue'
import * as IntegrationPropertyMapDirections from '../../../constants/integration-property-map-directions'
import _ from 'lodash'

export default {
    components: {
        DirectionSelector,
    },
    props: {
        model: {
            type: Object,
            required: true
        },
        integrationNameWithCasing: {
            type: String,
            required: true
        },
        staticName: {
            type: String,
            required: true
        },
        defaultMapperSettings: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        IntegrationPropertyMapDirections,
    }),
  created() {
    // Initialize mapper_settings if it doesn't exist
    if (!this.model.settings.mapper_settings) {
      this.$set(this.model.settings, 'mapper_settings', {});
    }

    // Initialize the contact mapping if it doesn't exist
    if (!this.model.settings.mapper_settings.contact) {
      const defaultContactMapperSettings = _.cloneDeep(this.defaultMapperSettings.contact)

      // If the old filter keys are set, update the directions for the new `settings.mapper_settings.contact` object
      for (const operation of defaultContactMapperSettings) {
        if (operation.internal_value === 'contact.create_v2') {
          operation.direction = this.model.settings.filters?.filter_never_create_new_contact ? IntegrationPropertyMapDirections.DIRECTION_INBOUND : IntegrationPropertyMapDirections.DIRECTION_BI
          continue
        }

        if (operation.internal_value === 'contact.update_v2') {
          if (this.model.settings.filters?.filter_never_update_contact === undefined) {
            operation.direction = IntegrationPropertyMapDirections.DIRECTION_INBOUND
          } else {
            operation.direction = this.model.settings.filters?.filter_never_update_contact ? IntegrationPropertyMapDirections.DIRECTION_INBOUND : IntegrationPropertyMapDirections.DIRECTION_BI
          }
        }
      }

      this.$set(this.model.settings.mapper_settings, 'contact', _.cloneDeep(defaultContactMapperSettings))
    }

    // Fallback: migrate legacy contact.create/contact.update to contact.create_v2/contact.update_v2 if needed
    else {
      const contactOps = this.model.settings.mapper_settings.contact
      const hasCreateV2 = contactOps.some(op => op.internal_value === 'contact.create_v2')
      const hasUpdateV2 = contactOps.some(op => op.internal_value === 'contact.update_v2')
      const hasLegacyCreate = contactOps.find(op => op.internal_value === 'contact.create')
      const hasLegacyUpdate = contactOps.find(op => op.internal_value === 'contact.update')
      if ((!hasCreateV2 && hasLegacyCreate) || (!hasUpdateV2 && hasLegacyUpdate)) {
        const defaults = _.cloneDeep(this.defaultMapperSettings.contact)
        if (!hasCreateV2 && hasLegacyCreate) {
          const tmpl = defaults.find(op => op.internal_value === 'contact.create_v2')
          if (tmpl) {
            tmpl.direction = hasLegacyCreate.direction
          }
        }
        if (!hasUpdateV2 && hasLegacyUpdate) {
          const tmpl = defaults.find(op => op.internal_value === 'contact.update_v2')
          if (tmpl) {
            tmpl.direction = hasLegacyUpdate.direction
          }
        }
        this.$set(this.model.settings.mapper_settings, 'contact', _.cloneDeep(defaults))
      }
    }

    // Initialize the communication call mapping if it doesn't exist
    if (!this.model.settings.mapper_settings.call) {
      const defaultCallMapperSettings = _.cloneDeep(this.defaultMapperSettings.call)

      for (const operation of defaultCallMapperSettings) {
        // For the deprecated `send_call_events` setting, if it's enabled, calls are sent as Engagement (Call Communication) and Timeline (Integration Activity) in HubSpot
        if (this.model.settings.send_call_events) {
          operation.enabled = true
          continue
        }

        // If the deprecated `send_call_events` setting is disabled, calls are only sent as Engagement (Call Communication) in HubSpot
        if (!this.model.settings.send_call_events && (operation.internal_value === 'call.dispatched' && operation.crm_value === 'engagement')) {
          operation.enabled = true
          continue
        }

        // If the deprecated `send_call_events` setting is disabled, calls are not sent as Timeline Events (Integration Activity) in HubSpot
        if (!this.model.settings.send_call_events && (operation.internal_value === 'call.dispatched' && operation.crm_value === 'timeline')) {
          operation.enabled = false
        }
      }

      this.$set(this.model.settings.mapper_settings, 'call', _.cloneDeep(defaultCallMapperSettings))
    }

    // Initialize the communication sms mapping if it doesn't exist
    if (!this.model.settings.mapper_settings.sms) {
      const defaultSmsMapperSettings = _.cloneDeep(this.defaultMapperSettings.sms)

      for (const operation of defaultSmsMapperSettings) {
        // For the deprecated `send_sms_as_note` setting, if it's enabled, SMS are sent as Engagement (SMS Communication) and Timeline Events (Integration Activity) in HubSpot
        if (this.model.settings.send_sms_as_note) {
          operation.enabled = true
          continue
        }

        // If the deprecated `send_sms_as_note` setting is disabled, SMS are only sent as Timeline Events (Integration Activity) in HubSpot
        if (!this.model.settings.send_sms_as_note && (operation.internal_value === 'sms.dispatched' && operation.crm_value === 'timeline')) {
          operation.enabled = true
          continue
        }

        // If the deprecated `send_sms_as_note` setting is disabled, SMS are not sent as Engagement (SMS Communication) in HubSpot
        if (!this.model.settings.send_sms_as_note && (operation.internal_value === 'sms.dispatched' && operation.crm_value === 'engagement')) {
          operation.enabled = false
          continue
        }
      }

      this.$set(this.model.settings.mapper_settings, 'sms', _.cloneDeep(defaultSmsMapperSettings))
    }
  },
  mounted() {
    // Listen for reset-tab events from root
    this.$root.$on('integration-settings-reset-tab', this.handleResetTab)
  },
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('integration-settings-reset-tab', this.handleResetTab)
  },
  methods: {
        updateDirection(index, direction) {
            // Restrict to INBOUND and BI-DIRECTIONAL for both contact.update and contact.create
            if (direction === IntegrationPropertyMapDirections.DIRECTION_OUTBOUND) {
                direction = IntegrationPropertyMapDirections.DIRECTION_BI; // Reset to bi-directional
            }

            this.model.settings.mapper_settings.contact[index].direction = direction
        },
        handleResetTab() {
          // Reset HubSpot-specific mapper settings
          this.model.settings.mapper_settings.contact = _.cloneDeep(this.defaultMapperSettings.contact)
          this.model.settings.mapper_settings.user = _.cloneDeep(this.defaultMapperSettings.user)

          // Show success notification
          this.$notify({
            title: `${this.integrationNameWithCasing} Integration`,
            message: 'The Synchronization tab has been reset to default values',
            type: 'success'
          })
        }
    },
}
</script>

<style scoped>
.main-wrapper {
    width: 100%;
}

.section-subheader {
    font-size: 16px;
}

.w-100 {
    width: 100%;
}

.align-center {
    align-items: center;
}
</style>
